/**
 * trackInteraction — fire-and-forget interaction logger.
 *
 * Every call records one event to the interaction_log table via POST /v1/interactions.
 * Batches events and flushes every 2 seconds or on page unload to reduce network chatter.
 * ML models consume this data to learn user behavior patterns per account.
 */
import { api } from './api';

interface InteractionEvent {
  event_type: string;
  entity_type?: string;
  entity_id?: string;
  page?: string;
  event_data?: Record<string, unknown>;
}

let buffer: InteractionEvent[] = [];
let flushTimer: number | null = null;
const FLUSH_INTERVAL_MS = 2_000;
const MAX_BUFFER = 50;

function flush(): void {
  if (buffer.length === 0) return;
  const events = buffer.splice(0, MAX_BUFFER);
  if (events.length === 1) {
    api('/v1/interactions', {
      method: 'POST',
      body: JSON.stringify(events[0]),
    }).catch(() => { /* silent */ });
  } else {
    api('/v1/interactions/batch', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }).catch(() => { /* silent */ });
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS) as unknown as number;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

export function trackInteraction(event: InteractionEvent): void {
  if (!event.event_type) return;
  if (!event.page && typeof window !== 'undefined') {
    event.page = window.location.pathname;
  }
  buffer.push(event);
  if (buffer.length >= MAX_BUFFER) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function trackPageView(page?: string): void {
  trackInteraction({
    event_type: 'page_view',
    page: page ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
  });
}

export function trackFeatureUse(feature: string, data?: Record<string, unknown>): void {
  trackInteraction({
    event_type: 'feature_use',
    event_data: { feature, ...data },
  });
}

export function trackEntityView(entityType: string, entityId: string, data?: Record<string, unknown>): void {
  trackInteraction({
    event_type: 'entity_view',
    entity_type: entityType,
    entity_id: entityId,
    event_data: data,
  });
}

export function trackSearch(query: string, resultCount?: number): void {
  trackInteraction({
    event_type: 'search',
    event_data: { query, result_count: resultCount },
  });
}
